import PageMeta from "@/components/PageMeta";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fetchCollection, fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import SectionFadeIn from "@/components/SectionFadeIn";
import { getRouteState } from "@/lib/navigation";
import MuralMaterialsSection from "@/components/MuralMaterialsSection";
import { MuralStyleGallery } from "@/components/murals/MuralStyleGallery";
import { MuralJourney } from "@/components/murals/MuralJourney";
import { MuralRoomGuide } from "@/components/murals/MuralRoomGuide";
import { MuralLandingConfigurator } from "@/components/murals/MuralLandingConfigurator";

const HERO_IMG =
  "https://images.unsplash.com/photo-1709544506583-cfb9845cdfb9?w=1400&q=80";

function isMuralProduct(product) {
  const productType = (product.productType || product.product_type || "").toLowerCase();
  return productType.includes("mural") || productType.includes("wall mural");
}

function sortProductsWithImagesFirst(list) {
  return [...list].sort((a, b) => {
    const aHas = a.images?.length > 0 ? 0 : 1;
    const bHas = b.images?.length > 0 ? 0 : 1;
    return aHas - bHas;
  });
}

export default function WallMuralsLanding() {
  const [products, setProducts] = useState([]);
  const location = useLocation();

  useEffect(() => {
    let active = true;

    async function loadMurals() {
      try {
        const collData = await fetchCollection("wall-murals");
        const collProds = (collData.collection?.products || []).filter(isMuralProduct);

        if (collProds.length > 0) {
          if (active) setProducts(sortProductsWithImagesFirst(collProds));
          return;
        }

        const fbData = await fetchProducts({ first: 250 });
        const fbProds = (fbData.products || []).filter(isMuralProduct);
        if (active) setProducts(sortProductsWithImagesFirst(fbProds));
      } catch (err) {
        console.error("WallMuralsLanding: primary fetch failed, trying fallback", err);
        try {
          const fbData = await fetchProducts({ first: 250 });
          const fbProds = (fbData.products || []).filter(isMuralProduct);
          if (active) setProducts(sortProductsWithImagesFirst(fbProds));
        } catch (innerErr) {
          console.error("WallMuralsLanding: fallback fetch also failed", innerErr);
          if (active) setProducts([]);
        }
      }
    }

    loadMurals();
    return () => { active = false; };
    // All referenced names are either module-level imports (fetchCollection,
    // fetchProducts, isMuralProduct, sortProductsWithImagesFirst), the stable
    // useState setter (setProducts), or locals scoped inside this effect.
    // No real dependencies — run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const withImages = products.filter((p) => p.images?.length > 0);
  const preview = withImages.slice(0, 4);

  return (
    <>
      <PageMeta
        title="Wall Murals"
        description="Made-to-measure wall murals from £14–£22 per m². Printed on high-quality wallpaper or peel & stick material, fitted to your exact wall dimensions. Browse designs or request a custom mural quote."
      />

      {/* ─── HERO ─── */}
      <section
        className="relative min-h-[68vh] md:min-h-[72vh] flex items-end pb-14 md:pb-18"
        data-testid="murals-hero"
      >
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Interior wall mural" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D14] via-[#0A0D14]/80 to-[#0A0D14]/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/30 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <p className="text-[11px] tracking-[0.35em] uppercase text-brand-gold font-body opacity-0 animate-fade-in-up">
            Made-to-Measure Wall Murals
          </p>
          <h1
            className="mt-4 font-heading text-[clamp(2.25rem,5.5vw,4.5rem)] tracking-tight leading-[0.92] text-brand-text max-w-[600px] opacity-0 animate-fade-in-up"
            style={{ animationDelay: "100ms" }}
            data-testid="murals-heading"
          >
            Walls That Tell
            <br />
            a Story
          </h1>
          <p
            className="mt-4 text-brand-text-secondary text-[15px] max-w-md font-body leading-relaxed opacity-0 animate-fade-in-up"
            style={{ animationDelay: "200ms" }}
          >
            Printed to your exact wall dimensions on premium wallpaper
            or vinyl. From &pound;14&ndash;&pound;22 per m&sup2; depending
            on material.
          </p>
          <div
            className="mt-8 flex flex-wrap gap-3 opacity-0 animate-fade-in-up"
            style={{ animationDelay: "300ms" }}
          >
            <Link
              to="/request-quote"
              state={getRouteState(location)}
              className="inline-flex items-center gap-2 bg-brand-gold text-brand-bg hover:bg-brand-gold-hover transition-colors duration-300 rounded-sm px-7 py-3 font-medium text-[13px] tracking-wide"
              data-testid="hero-quote-cta"
            >
              Get a Quote With Your Dimensions <ArrowRight size={15} />
            </Link>
            <Link
              to="/collections/wall-murals"
              state={getRouteState(location)}
              className="inline-flex items-center gap-2 border border-white/20 text-brand-text hover:border-brand-gold hover:text-brand-gold transition-colors duration-300 rounded-sm px-7 py-3 font-medium text-[13px] tracking-wide"
              data-testid="hero-browse-murals-cta"
            >
              Browse Designs
            </Link>
          </div>
        </div>
      </section>

      <MuralStyleGallery location={location} />

      {/* ─── FEATURED MURALS ─── */}
      {preview.length > 0 && (
        <section className="pb-20 md:pb-24" data-testid="murals-preview-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <SectionFadeIn>
              <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                  <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-brand-gold mb-3">
                    From the Collection
                  </p>
                  <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-brand-text">
                    Popular Murals
                  </h2>
                </div>
                <Link
                  to="/collections/wall-murals"
                  state={getRouteState(location)}
                  className="text-[12px] text-brand-text-secondary hover:text-brand-gold transition-colors duration-300 tracking-wide uppercase font-medium flex items-center gap-1.5 shrink-0"
                  data-testid="preview-view-all"
                >
                  View all <ArrowRight size={13} />
                </Link>
              </div>
            </SectionFadeIn>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {preview.map((product, i) => (
                <SectionFadeIn key={product.id} delay={i * 80}>
                  <ProductCard product={product} variant="mural" basePath="/collections/wall-murals" />
                </SectionFadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      <MuralJourney />
      <MuralRoomGuide location={location} />
      <MuralLandingConfigurator />
      <MuralMaterialsSection />

      {/* ─── QUOTE CTA BAR ─── */}
      <section className="border-y border-white/[0.05] bg-brand-surface/50" data-testid="collection-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl sm:text-[1.7rem] tracking-tight text-brand-text">
              Know your wall size?
            </h3>
            <p className="mt-1 text-[13px] text-brand-text-secondary">
              Send us the width and height and we&rsquo;ll quote it same day.
            </p>
          </div>
          <Link
            to="/request-quote"
            state={getRouteState(location)}
            className="inline-flex items-center gap-2 bg-brand-gold text-brand-bg hover:bg-brand-gold-hover transition-colors duration-300 rounded-sm px-8 py-3.5 font-medium text-[13px] tracking-wide shrink-0"
            data-testid="view-full-collection-cta"
          >
            Request a Mural Quote <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      {/* ─── CUSTOM MURAL ─── */}
      <section className="py-20 md:py-24 max-w-7xl mx-auto px-6 md:px-12" data-testid="custom-mural-section">
        <SectionFadeIn>
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-brand-gold mb-3">
              Your Image, Your Wall
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-brand-text leading-tight">
              Have Your Own Design?
            </h2>
            <p className="mt-3 text-[14px] text-brand-text-secondary leading-relaxed max-w-md mx-auto">
              Send us any image or brand graphic. Same made-to-measure
              process, same &pound;14&ndash;&pound;22/m&sup2; pricing.
              We handle sizing, scaling, and print.
            </p>
            <Link
              to="/request-quote"
              state={getRouteState(location)}
              className="inline-flex items-center gap-2 mt-7 border border-brand-gold/60 text-brand-gold hover:bg-brand-gold/10 transition-colors duration-300 rounded-sm px-7 py-3 font-medium text-[13px] tracking-wide"
              data-testid="custom-mural-quote-cta"
            >
              Request a Custom Mural Quote <ArrowRight size={15} />
            </Link>
          </div>
        </SectionFadeIn>
      </section>
    </>
  );
}
